import { MessageCircle, Send, Instagram, Facebook, Youtube, Music2 } from "lucide-react";
import { useSite } from "@/contexts/SiteContext";
import { RenderIcon } from "@/components/admin/IconPicker";

const PLATFORMS = [
  { key: "whatsapp_community", label: "WhatsApp", icon: MessageCircle, color: "#25D366" },
  { key: "telegram", label: "Telegram", icon: Send, color: "#0088CC" },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C" },
  { key: "tiktok", label: "TikTok", icon: Music2, color: "#000" },
  { key: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2" },
  { key: "youtube", label: "YouTube", icon: Youtube, color: "#FF0000" },
];

export default function Community() {
  const { site, sectionContent } = useSite();
  const c = sectionContent("community");
  if (!c) return null;
  const social = site?.social || {};
  // Cardurile definite din editor (content.items) au prioritate; altfel cădem pe rețelele din Setări→Social.
  // Păstrăm indexul ORIGINAL din array-ul stocat pentru `data-field=items.<i>.*` — altfel
  // editarea inline scrie în itemul greșit când lista are goluri (corupere de date).
  const customItems = Array.isArray(c.items)
    ? c.items.map((it, origIdx) => ({ it, origIdx })).filter(({ it }) => it && (it.label || it.url))
    : [];

  return (
    <section data-testid="community-section" className="py-24 lg:py-32" style={{ background: "var(--ct-bg)" }}>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 text-center">
        <div className="flex items-center justify-center gap-4 mb-4">
          <span className="gold-rule" />
          <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c.eyebrow || "Comunitate"}</span>
          <span className="gold-rule" />
        </div>
        <h2 data-el="title" data-field="title" className="font-serif text-4xl sm:text-5xl leading-tight">{c.title || "Intră în comunitatea Terapeut Cristian"}</h2>
        {c.subtitle && (
          <p data-el="subtitle" data-field="subtitle" className="text-base leading-relaxed mt-4 max-w-2xl mx-auto" style={{ color: "var(--ct-text-muted)" }}>{c.subtitle}</p>
        )}
        {customItems.length > 0 ? (
          /* Carduri definite din editor (content.items) */
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 max-w-4xl mx-auto">
            {customItems.map(({ it, origIdx }) => (
              <a
                key={origIdx}
                href={it.url || "#"}
                target="_blank"
                rel="noreferrer"
                className="card-premium px-3 py-5 flex flex-col items-center gap-2 transition-all hover:!border-[var(--ct-gold)]"
                data-testid={`community-item-${origIdx}`}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--ct-surface-2)" }}>
                  <RenderIcon value={it.icon || "Users"} size={18} color="var(--ct-gold)" />
                </div>
                <span data-el="item_title" data-field={`items.${origIdx}.label`} className="text-xs font-medium">{it.label}</span>
                {it.desc && <span data-el="item_body" data-field={`items.${origIdx}.desc`} className="text-[11px]" style={{ color: "var(--ct-text-muted)" }}>{it.desc}</span>}
              </a>
            ))}
          </div>
        ) : (
          /* Fallback: doar platformele configurate — fără carduri gri moarte și fără instrucțiuni de admin în public */
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 max-w-4xl mx-auto">
            {PLATFORMS.filter((p) => social[p.key]).map((p) => {
              const url = social[p.key];
              const Icon = p.icon;
              return (
                <a
                  key={p.key}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="card-premium px-3 py-5 flex flex-col items-center gap-2 transition-all hover:!border-[var(--ct-gold)]"
                  data-testid={`community-${p.key}`}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--ct-surface-2)" }}>
                    <Icon size={18} style={{ color: p.color }} />
                  </div>
                  <span className="text-xs font-medium">{p.label}</span>
                </a>
              );
            })}
          </div>
        )}
        {customItems.length === 0 && !Object.values(social).some(Boolean) && (
          <p className="text-sm mt-8 max-w-md mx-auto" style={{ color: "var(--ct-text-muted)" }}>
            Comunitatea se deschide în curând. Lasă-ți emailul în josul paginii și afli prima dată când pornim.
          </p>
        )}
      </div>
    </section>
  );
}
